import { useState, useEffect, useRef, useCallback } from 'react';
import { formatTime } from '../lib/dates';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';

/* ============================================================================
   THE DRIVER'S SCREEN

   A driver is not an Eduvora user. They are a contractor with a cheap Android
   phone, driving a bus, probably on a patchy connection. So:

     • NO LOGIN. The link they get by SMS carries the trip token. That's it.
     • ONE BUTTON. Start sharing. Stop sharing. Nothing else on the screen.
     • HONEST ABOUT FAILURE. If GPS is off, or the signal drops, it SAYS SO in
       large type — because a driver who thinks it's working when it isn't is
       worse than no tracking at all. Parents would be watching a dead dot.
     • A WAKE LOCK, so the phone doesn't sleep and silently stop sharing.
   ========================================================================== */

type State = 'checking' | 'ready' | 'sharing' | 'ended' | 'denied' | 'invalid';

export function DriverPage() {
  const { token = '' } = useParams();

  const [state, setState] = useState<State>('checking');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(0);
  const [lastAt, setLastAt] = useState<Date | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);

  const watchId = useRef<number | null>(null);
  const wakeLock = useRef<{ release: () => Promise<void> } | null>(null);

  // Is this trip actually running? If the school never started it — or already ended it —
  // the driver must find out NOW, not after an hour of driving.
  useEffect(() => {
    api.get<{ running: boolean }>(`/api/drive/${token}`)
      .then((r) => setState(r.running ? 'ready' : 'invalid'))
      .catch(() => setState('invalid'));
  }, [token]);

  const stopSharing = useCallback(() => {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    void wakeLock.current?.release().catch(() => {});
    wakeLock.current = null;
  }, []);

  useEffect(() => stopSharing, [stopSharing]);

  const startSharing = async () => {
    if (!('geolocation' in navigator)) {
      setError('This phone can’t share its location.');
      return;
    }

    // Keep the screen awake. Without this, the phone sleeps, the GPS stops, and parents watch a
    // frozen dot believing it's live. Not supported everywhere — hence the catch.
    try {
      const nav = navigator as Navigator & {
        wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> };
      };
      if (nav.wakeLock) wakeLock.current = await nav.wakeLock.request('screen');
    } catch { /* not fatal — sharing still works while the screen is on */ }

    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        setError(null);
        setAccuracy(pos.coords.accuracy);

        void api.post(`/api/drive/${token}/position`, {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracyMetres: pos.coords.accuracy,
          speedKmh: pos.coords.speed != null ? pos.coords.speed * 3.6 : null,
        })
          .then(() => { setSent((n) => n + 1); setLastAt(new Date()); })
          // A dropped POST is normal on a bus. Don't alarm the driver — the next fix will land.
          .catch(() => {});
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setState('denied');
          stopSharing();
        } else {
          setError('Lost the GPS signal. Keep the phone near a window.');
        }
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );

    setState('sharing');
  };

  const endTrip = async () => {
    stopSharing();
    await api.post(`/api/drive/${token}/end`, {}).catch(() => {});
    setState('ended');
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0A1220', color: '#edf1f7',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 24, textAlign: 'center', fontFamily: 'system-ui, sans-serif',
    }}>
      {state === 'checking' && <p style={{ fontSize: 18, opacity: 0.7 }}>Checking…</p>}

      {state === 'invalid' && (
        <Panel>
          <h1 style={{ fontSize: 24, marginBottom: 10 }}>This trip isn’t running</h1>
          <p style={{ opacity: 0.75, lineHeight: 1.6 }}>
            The school hasn’t started this trip, or it has already ended.
            Ask the office to start it and send you a new link.
          </p>
        </Panel>
      )}

      {state === 'denied' && (
        <Panel>
          <h1 style={{ fontSize: 24, marginBottom: 10, color: '#ff8a80' }}>Location is blocked</h1>
          <p style={{ opacity: 0.85, lineHeight: 1.6 }}>
            Parents can’t see the bus. Allow location for this site in your browser settings,
            then reload this page.
          </p>
        </Panel>
      )}

      {state === 'ready' && (
        <Panel>
          <h1 style={{ fontSize: 26, marginBottom: 8 }}>Ready to start</h1>
          <p style={{ opacity: 0.75, marginBottom: 28, lineHeight: 1.6 }}>
            Tap below and keep this screen open. Parents will see the bus moving.
          </p>
          <BigButton onClick={startSharing} colour="#4caf50">Start sharing location</BigButton>
        </Panel>
      )}

      {state === 'sharing' && (
        <Panel>
          <div style={{
            width: 90, height: 90, borderRadius: '50%', margin: '0 auto 20px',
            background: 'rgba(76,175,80,.15)', border: '3px solid #4caf50',
            display: 'grid', placeItems: 'center',
            animation: 'pulse 2s ease-in-out infinite',
          }}>
            <span style={{ fontSize: 34 }}>📍</span>
          </div>

          <h1 style={{ fontSize: 24, marginBottom: 6, color: '#4caf50' }}>Sharing location</h1>
          <p style={{ opacity: 0.7, marginBottom: 4 }}>
            {sent > 0
              ? `Parents can see the bus. Last update ${lastAt ? formatTime(lastAt) : ''}`
              : 'Getting a GPS fix…'}
          </p>

          {/* Honest about a bad fix. A 200m-accurate dot is not a bus. */}
          {accuracy != null && accuracy > 100 && (
            <p style={{ color: '#ffb74d', fontSize: 14, marginTop: 8 }}>
              Weak GPS ({Math.round(accuracy)}m). Keep the phone near the windscreen.
            </p>
          )}

          {error && (
            <p style={{ color: '#ffb74d', fontSize: 15, marginTop: 12 }}>{error}</p>
          )}

          <p style={{ opacity: 0.5, fontSize: 13, marginTop: 20, marginBottom: 28 }}>
            Keep this screen on until you arrive.
          </p>

          <BigButton onClick={endTrip} colour="#c9a15a">I’ve arrived — stop sharing</BigButton>
        </Panel>
      )}

      {state === 'ended' && (
        <Panel>
          <h1 style={{ fontSize: 26, marginBottom: 10, color: '#4caf50' }}>✓ Trip finished</h1>
          <p style={{ opacity: 0.75, lineHeight: 1.6 }}>
            Location sharing has stopped and the bus is no longer visible to anyone.
            You can close this page.
          </p>
        </Panel>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%      { transform: scale(1.06); opacity: .85; }
        }
      `}</style>
    </div>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return <div style={{ maxWidth: 380, width: '100%' }}>{children}</div>;
}

/** Big, because a driver taps it with a thumb, in a moving vehicle, possibly in the sun. */
function BigButton({ onClick, colour, children }: {
  onClick: () => void; colour: string; children: React.ReactNode;
}) {
  return (
    <button onClick={onClick} style={{
      width: '100%', padding: '20px 24px', fontSize: 18, fontWeight: 600,
      background: colour, color: '#0A1220', border: 'none', borderRadius: 14,
      cursor: 'pointer',
    }}>
      {children}
    </button>
  );
}
